// React Compiler opt-out: this file still reads mutable external state during render
// (legacy store object getters and/or current-time reads) that the useFluxStore bridge
// doesn't cover. Remove after migrating those reads (see flux-bridge-conversion skill).
'use no memo';

import React, {useMemo} from 'react';
import cx from 'classnames';
import type {QuestionModelV3} from '@albert-io/models';
import {useShallow} from 'zustand/react/shallow';
import Divider from './Divider.react';
import {useQuestionStore} from 'client/components/QuestionTypes/V2/useQuestionStores';
import {MovableItem} from '../../shared/MovableItem.react';
import {
  getSmallSupplement,
  SMALL_SUPPLEMENT_WRAPPER_CLASS,
  useIsSmallSupplementsEnabled
} from '../../shared/DraggableChip';

interface Props {
  item: {id: string; value: string; placedInSlot?: string | null};
  index: number;
  handleItemClick: (itemId: string, event: React.MouseEvent) => void;
  handleDividerClick: (dividerIndex: number) => void;
  question: QuestionModelV3;
}

export const Item = ({item, handleItemClick, handleDividerClick, index, question}: Props) => {
  const useStoreHook = useQuestionStore<'ordering'>(question);
  const isSmallSupplementsEnabled = useIsSmallSupplementsEnabled();
  const isSupplement = isSmallSupplementsEnabled && !!getSmallSupplement(item.value);

  const {activeItemId, isDragging, hoveredDividerId, draggedItemId, isSubmitted, correctAnswer} =
    // oxlint-disable-next-line react/react-compiler -- useStoreHook comes from setUpQuestionStore's registry (cached zustand hook keyed by store name); its identity is stable for a mounted component
    useStoreHook(
      useShallow((state) => ({
        activeItemId: state.activeItemId,
        isDragging: state.isDragging,
        hoveredDividerId: state.hoveredDividerId,
        draggedItemId: state.draggedItemId,
        isSubmitted: state.isSubmitted,
        correctAnswer: state.correctAnswer
      }))
    );

  const isCorrect = useMemo(() => {
    if (!correctAnswer) return false;

    // Slots define correct positions: position i is correct when the item here
    // matches validResponse[slots[i].id]
    const questionSlots =
      question?.getQuestionTypeAttributes()?.getIn(['ordering', 'slots'], [])?.toJS?.() ?? [];
    const slotId = questionSlots[index]?.id;

    return slotId ? correctAnswer[slotId] === item.id : false;
  }, [correctAnswer, item.id, index, question]);

  return (
    <div
      key={`${question?.getId?.()}-${item.id}`}
      className={cx('flex items-stretch', isSupplement && SMALL_SUPPLEMENT_WRAPPER_CLASS)}
    >
      {!isSubmitted && (
        <Divider
          id={`divider-${index}`}
          isVisible={isDragging || activeItemId !== null}
          isHovered={hoveredDividerId === `divider-${index}`}
          onClick={() => handleDividerClick(index)}
        />
      )}
      <div className={cx({'min-w-0 flex-1': isSupplement})}>
        <MovableItem
          key={item.id}
          item={item}
          isActive={activeItemId === item.id}
          isDragging={isDragging && draggedItemId === item.id}
          isSubmitted={isSubmitted}
          onClick={(e) => handleItemClick(item.id, e)}
          activeItemId={activeItemId}
          isCorrect={isCorrect}
          showIcon={false}
        />
      </div>
    </div>
  );
};
